/**
 * INTREC-1 — an integrated (terminal / App2App / networked PED) card sale that
 * the terminal APPROVED and the server has not recorded as a Payment yet.
 *
 * On the settle screen the approval used to live only in the component: a
 * reload, a network error or a cancelled manager approval dropped it, and the
 * card stayed charged with nothing on the check to say so. The screen now
 * writes the approval to this device's storage before posting it, keeps it on
 * screen until the server takes it, and brings it back when the same check's
 * settle screen opens again. Modelled on the kiosk's durable recorder
 * (kiosk-card-sale-recorder.service.ts), cut down to what a cashier-attended
 * screen needs: no timers, the cashier retries.
 *
 * Plain functions rather than a service so the settle screen's constructor —
 * which the specs call positionally — does not change.
 */
import { Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import type { EdgeBridgeService } from '../../../core/edge/edge-bridge.service';

export interface UnrecordedIntegratedSale {
  checkId: string;
  /**
   * Idempotency-Key the record POST rides. Minted when the terminal approves,
   * kept across every retry that got no answer, replaced only after the server
   * answered with a refusal (see `isUnansweredPaymentAttempt`).
   */
  idempotencyKey: string;
  /** The exact body posted to pos/checks/{id}/payments, frozen at approval. */
  payload: {
    method: 'Card';
    /** What the terminal charged — never the (live, editable) tendered amount. */
    amount: number;
    changeGiven: 0;
    provider?: string;
    authCode?: string;
    rrn?: string;
  };
  currency: string;
  capturedAt: number;
}

/**
 * PAY-1 / PAY-4 — a terminal sale this device asked the terminal to charge and
 * has no final answer for yet. Written BEFORE the terminal is asked, so the
 * charged amount and the key the edge echoes back are fixed before any money
 * can move, and a reload or a navigation mid-sale leaves a trace instead of a
 * clean slate.
 *
 * It ends one of three ways: approved (it becomes an UnrecordedIntegratedSale
 * and is recorded), decided with no charge (declined, cancelled, or refused
 * before the terminal was reached — forgotten), or undecided (`unknownSince`
 * set). An undecided attempt blocks the next terminal charge on this check
 * until the terminal reports back or a cashier who looked at it clears it.
 */
export interface IntegratedSaleAttempt {
  checkId: string;
  /**
   * Sent to `payment:start-sale` as its idempotencyKey. The edge echoes it on
   * `payment:sale-completed`, which is how a late outcome finds its sale.
   */
  terminalKey: string;
  /** What the terminal is asked to charge — the only amount an approval may record. */
  amount: number;
  currency: string;
  provider?: string;
  startedAt: number;
  /** Set when this page stopped waiting without a decided outcome: the card may have been charged. */
  unknownSince?: number;
  /** The last thing the page was told about the sale, for the banner. */
  lastError?: string;
}

const STORAGE_KEY = 'ipos.pos.unrecordedIntegratedSales';
const ATTEMPTS_STORAGE_KEY = 'ipos.pos.integratedSaleAttempts';

/**
 * How long an unrecorded approval is kept. Matches the server's idempotency
 * TTL: past it, resending under the same key could record a sale the server
 * already took, and the trading day it belongs to is over anyway.
 */
const KEEP_MS = 24 * 60 * 60 * 1000;

/**
 * Error codes after which the terminal cannot have charged the card, because
 * the sale never reached it. The edge's own list (kPreLaunchPaymentErrorCodes)
 * also has 'provider-error', which is left out on purpose: the edge uses it for
 * "no response from the payment plugin" too, and that happens after the
 * terminal app was launched. `start-refused` is the dialog's code for a
 * server-driven start the API refused with an answer (terminal busy, not
 * configured, rejected by the gateway), which pushes nothing to the PED.
 */
const NOT_CHARGED_ERROR_CODES: readonly string[] = [
  'args-invalid', 'sale-in-progress', 'provider-not-configured', 'UTAP_SDK_MISSING', 'NO_ACTIVITY',
  'UNSUPPORTED_PROVIDER', 'PAYMENT_ERROR', 'NGENIUS_APP_MISSING',
  'bridge-unavailable', 'unknown-handler', 'start-refused',
];

/**
 * PAY-4 — true when a sale ended without a decided outcome, so the card may
 * have been charged: a timeout, a lost poll, a terminal result the SDK does not
 * define, a server session that ended in Error. "Try again" is the wrong advice
 * for these — the cashier has to look at the terminal first.
 */
export function isUndecidedTerminalOutcome(
  r: { status?: string; errorCode?: string | null } | null | undefined,
): boolean {
  if (!r) return false;
  if (r.status === 'approved' || r.status === 'declined' || r.status === 'cancelled') return false;
  return !NOT_CHARGED_ERROR_CODES.includes(r.errorCode ?? '');
}

/** Same shape as the settle screen's other per-press keys (`cc-`, `sc-`, …). */
export function newIntegratedRecordKey(checkId: string): string {
  return newKey('ti', checkId);
}

/** The key a terminal sale runs under (`ts-…`), minted once per Charge press. */
export function newTerminalSaleKey(checkId: string): string {
  return newKey('ts', checkId);
}

function newKey(prefix: string, checkId: string): string {
  const c: any = (globalThis as any).crypto;
  const rand = c?.randomUUID ? c.randomUUID() : `${Date.now()}${Math.random().toString(36).slice(2)}`;
  // Backend requires ^[A-Za-z0-9_-]{8,64}$.
  return `${prefix}-${checkId}-${rand}`.replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 64);
}

/**
 * The record for an approved attempt. The amount comes from the attempt — what
 * the terminal was asked to charge — never from anything on screen now.
 */
export function saleFromAttempt(
  attempt: IntegratedSaleAttempt,
  outcome: { provider?: string; approvalCode?: string; rrn?: string },
): UnrecordedIntegratedSale {
  return {
    checkId: attempt.checkId,
    idempotencyKey: newIntegratedRecordKey(attempt.checkId),
    payload: {
      method: 'Card',
      amount: attempt.amount,
      changeGiven: 0,
      provider: outcome.provider ?? attempt.provider ?? undefined,
      authCode: outcome.approvalCode || undefined,
      rrn: outcome.rrn || undefined,
    },
    currency: attempt.currency,
    capturedAt: Date.now(),
  };
}

/**
 * PAY-3 / PAY-4 — the app-wide half of hearing a late outcome. The edge repeats
 * every bridge sale's outcome as `payment:sale-completed`; this writes one
 * that belongs to a stored attempt into device storage, whatever screen is
 * open. A late approval arriving while the cashier is elsewhere (or on a page
 * that reloaded) becomes an unrecorded sale, shown with Retry the next time
 * that check's settle screen opens, instead of being lost. A settle screen that
 * is open reacts to the same event itself and records at once
 * (`SettleScreenComponent.onBridgeSaleCompleted`); both write per check, so
 * the two never leave duplicates.
 */
export function captureIntegratedSaleOutcome(
  event: { status?: string; idempotencyKey?: string | null; provider?: string; approvalCode?: string; rrn?: string; errorCode?: string | null } | null | undefined,
): void {
  if (!event?.status || !event.idempotencyKey) return;
  const attempt = readAttempts().find(a => a.terminalKey === event.idempotencyKey);
  if (!attempt) return;
  if (event.status === 'approved') {
    if (loadUnrecordedIntegratedSale(attempt.checkId)) {
      // Cannot happen through the screen (it starts no sale while one is
      // unrecorded); if it does, keep both visible rather than overwrite.
      console.error(
        `[Settle] late terminal approval for check ${attempt.checkId} (amount ${attempt.amount}, auth ${event.approvalCode || '?'},`
        + ` rrn ${event.rrn || '?'}) arrived while another approval is unrecorded; staff must reconcile it`,
      );
      return;
    }
    storeUnrecordedIntegratedSale(saleFromAttempt(attempt, event));
    forgetIntegratedSaleAttempt(attempt.checkId);
    return;
  }
  if (!isUndecidedTerminalOutcome(event)) forgetIntegratedSaleAttempt(attempt.checkId);
}

/** Starts `captureIntegratedSaleOutcome` for the app's lifetime; call once at boot. */
export function watchIntegratedSaleOutcomes(edge: {
  isEdgeEmbedded$: Observable<boolean>;
  event$: EdgeBridgeService['event$'];
}): void {
  // Subscribed the moment the bridge reports itself embedded, which is before
  // it tells the edge it is ready and the edge flushes what it buffered.
  edge.isEdgeEmbedded$.pipe(
    filter(Boolean),
    take(1),
    switchMap(() => edge.event$('payment:sale-completed')),
  ).subscribe(e => captureIntegratedSaleOutcome(e));
}

export function loadIntegratedSaleAttempt(checkId: string): IntegratedSaleAttempt | null {
  return readAttempts().find(a => a.checkId === checkId) ?? null;
}

/** One attempt per check: the screen starts no second terminal sale while one is open or undecided. */
export function storeIntegratedSaleAttempt(attempt: IntegratedSaleAttempt): void {
  writeList(ATTEMPTS_STORAGE_KEY, [...readAttempts().filter(a => a.checkId !== attempt.checkId), attempt]);
}

export function forgetIntegratedSaleAttempt(checkId: string): void {
  writeList(ATTEMPTS_STORAGE_KEY, readAttempts().filter(a => a.checkId !== checkId));
}

/**
 * True when a record POST got no definitive answer, so the sale may already be
 * recorded and a retry MUST reuse the same key (the server then replays the
 * recorded payment instead of making a second one):
 *   • no response at all — ApiService maps it to status 0 / "Connection error",
 *   • 408 / 429 / 5xx with a status ApiService could carry (a gateway timeout
 *     can leave the API still running the request),
 *   • the idempotency filter's own 409: an earlier request with this key is
 *     still being processed.
 * Anything else is the server's answer. It is cached against the key for 24h,
 * so resending under that key would only replay it — and a refusal recorded
 * nothing, so a fresh key cannot double-record.
 *
 * Note ApiService drops the HTTP status whenever the body is a structured
 * ApiResponse, so a structured 5xx lands in "answered". That is safe: the
 * server never caches a 5xx (it deletes the claim), so the old key and a new
 * one would run the request the same way.
 */
export function isUnansweredPaymentAttempt(
  res: { success?: boolean; error?: string; status?: unknown } | null | undefined,
): boolean {
  if (!res) return true;
  if (res.success) return false;
  const status = typeof res.status === 'number' ? res.status : null;
  if (status !== null && (status === 0 || status === 408 || status === 429 || status >= 500)) return true;
  const error = (res.error ?? '').toLowerCase();
  // IdempotencyAttribute's InProgress text; it carries no code to match on.
  return error.startsWith('connection error') || error.includes('still being processed');
}

export function loadUnrecordedIntegratedSale(checkId: string): UnrecordedIntegratedSale | null {
  return readAll().find(s => s.checkId === checkId) ?? null;
}

/** One unrecorded approval per check: the screen refuses a new terminal charge while one is waiting. */
export function storeUnrecordedIntegratedSale(sale: UnrecordedIntegratedSale): void {
  writeAll([...readAll().filter(s => s.checkId !== sale.checkId), sale]);
}

export function forgetUnrecordedIntegratedSale(checkId: string): void {
  writeAll(readAll().filter(s => s.checkId !== checkId));
}

// Every storage access is guarded: storage can be unavailable or full, and the
// record attempt (and the on-screen banner) must still work when it is.

function readAll(): UnrecordedIntegratedSale[] {
  return readList<UnrecordedIntegratedSale>(
    STORAGE_KEY,
    s => typeof s.idempotencyKey === 'string',
    s => s.capturedAt,
    // Staff-facing: a charged card this device could never get recorded.
    s => `[Settle] giving up on recording the terminal-approved card sale for check ${s.checkId}`
      + ` (amount ${s.payload?.amount}, auth ${s.payload?.authCode || '?'}, rrn ${s.payload?.rrn || '?'}); staff must reconcile it`,
  );
}

function writeAll(sales: UnrecordedIntegratedSale[]): void {
  writeList(STORAGE_KEY, sales);
}

function readAttempts(): IntegratedSaleAttempt[] {
  return readList<IntegratedSaleAttempt>(
    ATTEMPTS_STORAGE_KEY,
    a => typeof a.terminalKey === 'string',
    a => a.startedAt,
    // Staff-facing: nobody on this device ever learned whether this card was charged.
    a => `[Settle] giving up on the card sale with no terminal result for check ${a.checkId}`
      + ` (amount ${a.amount}, key ${a.terminalKey}); staff must check the terminal`,
  );
}

function readList<T extends { checkId: string }>(
  key: string,
  isValid: (item: T) => boolean,
  keptSince: (item: T) => number,
  expiredMessage: (item: T) => string,
): T[] {
  let items: T[];
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) || '[]');
    items = Array.isArray(parsed)
      ? (parsed as T[]).filter(s => !!s && typeof s.checkId === 'string' && isValid(s))
      : [];
  } catch {
    return [];
  }
  const cutoff = Date.now() - KEEP_MS;
  const kept = items.filter(s => keptSince(s) >= cutoff);
  if (kept.length !== items.length) {
    for (const s of items.filter(x => keptSince(x) < cutoff)) console.error(expiredMessage(s));
    writeList(key, kept);
  }
  return kept;
}

function writeList<T>(key: string, items: T[]): void {
  try {
    if (items.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // Storage unavailable: the in-memory record and its banner still work.
  }
}
